import React from 'react';
import { ArrowLeft, Package, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useProducts } from '../hooks/useProducts';

const ProductCatalogue = () => {
  const navigate = useNavigate();
  const { products, loading } = useProducts();
  
  const scrollToForm = () => {
    window.location.href = '/#contact-form';
  };

  const handleInquireNow = (product) => {
    const params = new URLSearchParams({
      product: product.name,
      category: product.category
    });
    navigate(`/contact?${params.toString()}#inquiry-form`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">Back to Home</span>
              </a>
              <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Product Catalogue</h1>
                <p className="text-xs text-gray-600">{products.length} Premium Products</p>
              </div>
            </div>
            <Button onClick={scrollToForm} className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
              Request Quote
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-block p-3 bg-blue-100 rounded-full mb-4">
            <Package className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Complete Product Range
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our comprehensive catalogue of premium beauty products across Hair Care, Colour Cosmetics, Skin Care, and Body Care
          </p>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="mt-4 text-gray-600">Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No products available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden group">
                  <div className="h-56 overflow-hidden bg-gray-100 relative">
                    <img 
                      src={product.image} 
                      alt={product.altText || `${product.name} - ${product.category} manufacturing India`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3">
                      {product.shadeCount && (
                        <Badge className="bg-blue-600 text-white">
                          {product.shadeCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <div className="mb-3">
                      <Badge variant="outline" className="text-xs mb-2">
                        {product.category}
                      </Badge>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {product.name}
                      </h3>
                      {product.description && (
                        <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                      )}
                    </div>
                    
                    {product.shades && product.shades.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-2">Available Shades:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {product.shades.slice(0, 6).map((shade, idx) => (
                            <div 
                              key={idx}
                              className="w-7 h-7 rounded-full border-2 border-gray-300 hover:scale-110 transition-transform cursor-pointer"
                              style={{ backgroundColor: shade.color }}
                              title={shade.name}
                            />
                          ))}
                          {product.shades.length > 6 && (
                            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-600">
                              +{product.shades.length - 6}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      onClick={() => handleInquireNow(product)}
                      size="sm"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Inquire Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Manufacturing?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get in touch with our team to discuss your requirements and receive a custom quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Request Quote
            </Button>
            <Button 
              onClick={() => window.location.href = '/contact'}
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-gray-400 mb-4">
            &copy; {new Date().getFullYear()} VV CosmoTech. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <a href="/" className="text-gray-400 hover:text-white transition-colors">Home</a>
            <a href="/products" className="text-gray-400 hover:text-white transition-colors">Products</a>
            <a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            <a href="/admin/login" className="text-gray-500 hover:text-blue-400 transition-colors">Admin</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductCatalogue;
